/**
 * URL firmada para leer documentos de Storage.
 *
 * Las tablas guardan la URL PUBLICA del objeto (`/storage/v1/object/public/<bucket>/<ruta>`) que
 * devolvio `getPublicUrl` al subirlo. Esa URL sirve de identificador, pero no se debe usar para
 * leer: los buckets de documentos van a dejar de ser publicos y solo se leen con URL firmada.
 *
 * Este modulo es la UNICA forma de convertir esa URL guardada (o un bucket + ruta) en una URL
 * firmada de caducidad corta. Es puro: recibe el cliente de storage, asi se prueba sin Supabase.
 * El envoltorio con el cliente real y el hook de React viven en `@/hooks/useUrlFirmada`.
 *
 * Reglas:
 *   - Una URL que no es de Storage (externa, `blob:`, `data:`) se devuelve igual.
 *   - Una URL de Storage de OTRO proyecto (filas de Preview que apuntan a Produccion) se devuelve
 *     igual: firmarla aqui daria un token valido para un objeto que no existe.
 *   - Si la firma falla y la entrada era una URL absoluta, se devuelve la original. Mientras el
 *     bucket siga publico eso mantiene la pantalla funcionando; cuando sea privado, el error lo
 *     da Storage y no una pantalla en blanco.
 */

/** Buckets de documentos que pasan a privados (fase 3). Solo informativo para pantallas. */
export const BUCKETS_DOCUMENTOS_PRIVADOS = [
  "documentos",
  "ceps",
  "ceps_stp",
  "facturas",
  "evidencias_efectivo",
  "estados_cuenta",
  "legacy-uploads",
  "cuentas_canceladas_docs",
] as const;

/** Caducidad por defecto de una URL firmada para ver un documento: 10 minutos. */
export const CADUCIDAD_FIRMA_SEG = 600;

/** Margen antes de caducar en el que una firma en cache ya no se reutiliza. */
const MARGEN_CACHE_MS = 60_000;

export interface RefStorage {
  bucket: string;
  path: string;
}

export interface ClienteStorageFirma {
  from(bucket: string): {
    createSignedUrl(
      path: string,
      expiresIn: number,
      options?: { download?: string | boolean },
    ): Promise<{ data: { signedUrl: string } | null; error: unknown }>;
  };
}

export interface OpcionesFirma {
  /** Segundos de vida de la firma. Default `CADUCIDAD_FIRMA_SEG`. */
  expiraEn?: number;
  /** `true` o nombre de archivo: la URL firmada fuerza descarga. */
  descarga?: string | boolean;
  /** Host del proyecto de esta sesion (`new URL(VITE_SUPABASE_URL).host`). */
  hostProyecto?: string;
  /** Reloj inyectable para pruebas. */
  ahora?: () => number;
}

const RE_OBJETO = /\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/?#]+)\/([^?#]+)/;
const RE_RENDER = /\/storage\/v1\/render\/image\/(?:public|sign|authenticated)\/([^/?#]+)\/([^?#]+)/;

function decodificar(ruta: string): string {
  try {
    return decodeURIComponent(ruta);
  } catch {
    return ruta;
  }
}

/** Host de una URL absoluta, o "" si no lo es. */
function hostDe(url: string): string {
  if (!/^https?:\/\//i.test(url)) return "";
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
}

/**
 * Bucket y ruta de una URL de Storage (`object/public|sign|authenticated` o `render/image/...`).
 * `null` si la URL no es de Storage. No mira el host.
 */
export function parsearUrlStorage(url: string | null | undefined): RefStorage | null {
  if (!url) return null;
  const m = url.match(RE_OBJETO) ?? url.match(RE_RENDER);
  if (!m) return null;
  const path = decodificar(m[2]).replace(/^\/+/, "");
  if (!path) return null;
  return { bucket: decodificar(m[1]), path };
}

/** La URL es absoluta y apunta a un host distinto del proyecto de esta sesion. */
export function esDeOtroProyecto(url: string, hostProyecto?: string): boolean {
  if (!hostProyecto) return false;
  const h = hostDe(url);
  return h !== "" && h !== hostProyecto;
}

/** La URL apunta a un bucket que deja de ser publico. */
export function esDocumentoPrivado(url: string | null | undefined): boolean {
  const ref = parsearUrlStorage(url);
  return !!ref && (BUCKETS_DOCUMENTOS_PRIVADOS as readonly string[]).includes(ref.bucket);
}

/**
 * Que hay que firmar para una entrada: la referencia, o `null` si la entrada se usa tal cual
 * (no es de Storage o es de otro proyecto).
 */
export function refAFirmar(
  entrada: string | RefStorage | null | undefined,
  hostProyecto?: string,
): RefStorage | null {
  if (!entrada) return null;
  if (typeof entrada !== "string") {
    const path = entrada.path?.replace(/^\/+/, "") ?? "";
    return entrada.bucket && path ? { bucket: entrada.bucket, path } : null;
  }
  if (esDeOtroProyecto(entrada, hostProyecto)) return null;
  // Ya firmada (`/object/sign/...?token=`): se usa tal cual, no se vuelve a firmar.
  if (/\/storage\/v1\/(?:object|render\/image)\/sign\/[^?#]+\?(?:[^#]*&)?token=/.test(entrada)) return null;
  return parsearUrlStorage(entrada);
}

interface EntradaCache {
  url: string;
  vence: number;
}

const cache = new Map<string, EntradaCache>();

/** Vacia la cache de firmas (pruebas y cierre de sesion). */
export function limpiarCacheFirmas(): void {
  cache.clear();
}

function claveCache(ref: RefStorage, descarga: string | boolean | undefined): string {
  return `${ref.bucket}\u0000${ref.path}\u0000${descarga === undefined ? "" : String(descarga)}`;
}

/**
 * URL lista para abrir o mostrar.
 *   - Storage del proyecto: URL firmada (cacheada hasta 1 min antes de caducar).
 *   - Otra URL: la misma.
 *   - Firma fallida: la original si era absoluta; `null` si era bucket + ruta.
 */
export async function firmarUrl(
  cliente: ClienteStorageFirma,
  entrada: string | RefStorage | null | undefined,
  opciones: OpcionesFirma = {},
): Promise<string | null> {
  if (!entrada) return null;
  const ref = refAFirmar(entrada, opciones.hostProyecto);
  if (!ref) return typeof entrada === "string" ? entrada : null;

  const expiraEn = Math.max(30, Math.floor(opciones.expiraEn ?? CADUCIDAD_FIRMA_SEG));
  const ahora = opciones.ahora ?? Date.now;
  const clave = claveCache(ref, opciones.descarga);
  const enCache = cache.get(clave);
  if (enCache && enCache.vence - ahora() > MARGEN_CACHE_MS) return enCache.url;

  const respaldo = typeof entrada === "string" && /^https?:\/\//i.test(entrada) ? entrada : null;
  try {
    const opts = opciones.descarga === undefined ? undefined : { download: opciones.descarga };
    const { data, error } = await cliente.from(ref.bucket).createSignedUrl(ref.path, expiraEn, opts);
    if (error || !data?.signedUrl) return respaldo;
    cache.set(clave, { url: data.signedUrl, vence: ahora() + expiraEn * 1000 });
    return data.signedUrl;
  } catch {
    return respaldo;
  }
}
